/**
 * Meters: their lifecycle (arqam doc 41), the round order and their numbers.
 * Shared by the committee phone app and the desk (docs/changes/41).
 */

export type MeterStatus =
  | 'CONNECTED'
  | 'SUSPENDED'
  | 'DAMAGED'
  | 'REPLACED'
  | 'REMOVED'
  | 'CLOSED';

export const METER_STATUS_LABELS: Record<MeterStatus, string> = {
  CONNECTED: 'مربوط',
  SUSPENDED: 'موقوف',
  DAMAGED: 'معطّل',
  REPLACED: 'مُستبدَل',
  REMOVED: 'مُسترجَع',
  CLOSED: 'مغلق',
};

/** What each status means, shown when choosing it. */
export const METER_STATUS_HINTS: Record<MeterStatus, string> = {
  CONNECTED: 'يعمل عادياً وتصدر فواتيره.',
  SUSPENDED: 'قُطع الماء لعدم الأداء، ويُعاد ربطه بعد الأداء.',
  DAMAGED: 'معطّل في انتظار الإصلاح.',
  REPLACED: 'عُوّض بعداد آخر لنفس العضو (نهائي).',
  REMOVED: 'استرجعته الجمعية (نهائي).',
  CLOSED: 'أُغلق بطلب من العضو أو بعد مغادرته (نهائي).',
};

/** No change after these (arqam: METER_STATUS_FINAL). */
export const FINAL_STATUSES: MeterStatus[] = ['REPLACED', 'REMOVED', 'CLOSED'];

/** The statuses a meter can be moved to from `current`. */
export function nextStatuses(current: MeterStatus): MeterStatus[] {
  if (FINAL_STATUSES.includes(current)) return [];
  return (Object.keys(METER_STATUS_LABELS) as MeterStatus[]).filter(
    (s) => s !== current,
  );
}

/** arqam's status codes (its doc 41), in Arabic. */
export const METER_STATUS_ERRORS = {
  REASON_REQUIRED: 'اكتب سبب التغيير.',
  STATUS_UNCHANGED: 'العداد في هذه الحالة أصلاً.',
  METER_STATUS_FINAL: 'هذه الحالة نهائية: لا يمكن تغييرها.',
  REPLACEMENT_REQUIRED: 'اختر العداد الجديد (مربوط، لنفس العضو).',
  REPLACEMENT_NOT_EXPECTED: 'العداد الجديد لا يُختار إلا عند الاستبدال.',
  DATE_INVALID: 'تاريخ غير صحيح (لا يكون في المستقبل).',
};

/** Creating or editing a meter, and its photo (arqam docs 41, 47). */
export const METER_ERRORS = {
  'UNIQUE_VIOLATION:meters_meter_no_key': 'هذا الرقم مستعمل لعداد آخر.',
  METER_PHOTO_REQUIRED: 'صورة العداد إلزامية',
  PHOTO_WRONG_KIND: 'هذه ليست صورة عداد. التقط صورة العداد من جديد.',
  PHOTO_NOT_FOUND: 'تعذّر العثور على الصورة. التقطها من جديد.',
  MEMBER_LEFT: 'هذا العضو غادر الجمعية: لا يُضاف له عداد.',
};

/**
 * New round order after moving the meter at `index` by `delta` (-1 up, +1
 * down): the list renumbered 1..n, and only the meters whose `sortOrder`
 * changes. (Many meters share sortOrder 0, so the first move renumbers them.)
 */
export function moveInRound<T extends { id: string; sortOrder: number | null }>(
  meters: T[],
  index: number,
  delta: number,
): { order: T[]; changes: { id: string; sortOrder: number }[] } {
  const target = Math.max(0, Math.min(meters.length - 1, index + delta));
  if (target === index || index < 0 || index >= meters.length) {
    return { order: meters, changes: [] };
  }
  const order = [...meters];
  const [moved] = order.splice(index, 1);
  order.splice(target, 0, moved);
  const changes = order.flatMap((m, i) =>
    m.sortOrder === i + 1 ? [] : [{ id: m.id, sortOrder: i + 1 }],
  );
  return {
    order: order.map((m, i) => ({ ...m, sortOrder: i + 1 })),
    changes,
  };
}

/**
 * The place of a new meter: after the last one of the round. arqam's default
 * is 0, which sorts first: the reader would meet the new meter first.
 */
export const nextSortOrder = (sortOrders: (number | null)[]) =>
  Math.max(0, ...sortOrders.map((n) => n ?? 0)) + 1;

/**
 * One request that sets several meters' sortOrder, instead of one mutation
 * per meter (the first reorder can renumber the whole round, ~95 meters, and
 * each separate result would make urql refetch the list).
 */
export function reorderMutation(changes: { id: string; sortOrder: number }[]) {
  const params = changes
    .map((_, i) => `$id${i}: UUID!, $s${i}: Int!`)
    .join(', ');
  const fields = changes
    .map(
      (_, i) =>
        `m${i}: updateMeter(input: { id: $id${i}, patch: { sortOrder: $s${i} } }) { meter { id sortOrder } }`,
    )
    .join('\n');
  const variables = Object.fromEntries(
    changes.flatMap((c, i) => [
      [`id${i}`, c.id],
      [`s${i}`, c.sortOrder],
    ]),
  );
  return {
    query: `mutation ReorderMeters(${params}) {\n${fields}\n}`,
    variables,
  };
}

/**
 * Most meters have no real number: arqam stores a generated UUID, and
 * `Meter.label` shows the address instead. An empty number field does the
 * same for new meters.
 */
export const meterNoOrGenerated = (input: string) =>
  input.trim() || crypto.randomUUID();

/** A generated number (UUID) is not shown in the forms. */
export const isGeneratedMeterNo = (meterNo: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    meterNo,
  );

/** Today in Morocco ("YYYY-MM-DD"): the default and latest status date. */
export const todayInMorocco = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Casablanca' }).format(
    new Date(),
  );
